import { globalSingleton } from '../../utils/global-singleton'
import type { ConsentState } from '../../types/cookie-consent'

type Listener = (state: ConsentState) => void

/**
 * Holds the current consent snapshot and notifies subscribers. Listener
 * errors are isolated so one bad subscriber can't break the others (or the
 * consent flow).
 */
export class ConsentStore {
  private current: ConsentState | null = null
  private readonly listeners = new Set<Listener>()

  get(): ConsentState | null {
    return this.current
  }

  set(state: ConsentState): void {
    this.current = state
    for (const listener of this.listeners) {
      try {
        listener(state)
      } catch (error) {
        console.error('[montilla:cookie-consent] onChange listener failed:', error)
      }
    }
  }

  /** Subscribe to changes. Fires immediately if a state already exists. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    if (this.current) {
      try {
        listener(this.current)
      } catch (error) {
        console.error('[montilla:cookie-consent] onChange listener failed:', error)
      }
    }
    return () => {
      this.listeners.delete(listener)
    }
  }

  clear(): void {
    this.current = null
  }
}

/**
 * Pinned to `globalThis` so duplicate copies of the SDK share one consent
 * store — consistent with the montilla singleton and plugin registry.
 */
export const consentStore = globalSingleton<ConsentStore>(
  'cookie-consent-store',
  () => new ConsentStore(),
)

/**
 * Minimal controller surface the `montilla.cookieConsent` namespace delegates
 * to. Kept as an interface (not the concrete banner) so this light module —
 * imported by the always-present namespace — never pulls in the heavy banner /
 * styles code. The banner is only wired in from the dynamically-imported
 * `install` module, preserving the lazy-load boundary.
 */
export interface CookieConsentController {
  show(): void
  hide(): void
  accept(): void
  reset(): void
  hasConsent(): boolean
}

const controllerRef = globalSingleton<{ current: CookieConsentController | null }>(
  'cookie-consent-controller',
  () => ({ current: null }),
)

export function setCookieConsentController(controller: CookieConsentController | null): void {
  controllerRef.current = controller
}

export function getCookieConsentController(): CookieConsentController | null {
  return controllerRef.current
}
